<script setup lang="ts">
import { onBeforeMount, useField, watch } from '#imports'
import { LiveValidateType, type UIInputBaseProps, type UISelectItem, type UISelectItemGroup, type UseFieldEmits } from '@core/types'

export interface UISelectProps extends UIInputBaseProps {
    modelValue?: any
    items?: Array<UISelectItem | UISelectItemGroup>
    disabled?: HTMLSelectElement['disabled']
    autocomplete?: HTMLSelectElement['autocomplete']
    autofocus?: HTMLSelectElement['autofocus']
    multiple?: HTMLSelectElement['multiple']
    required?: HTMLSelectElement['required']
    size?: HTMLSelectElement['size']
}
const props = defineProps<UISelectProps>()
const emit = defineEmits<UseFieldEmits>()
defineSlots<{
    default?(): any
    item?(props: UISelectItem): any
    error?(): any
}>()

function getDefaultValue(item: UISelectItem) {
    if (item.type === 'option') return item.value

    return item.items?.[0]?.value
}

function getInitialValue() {
    if (props.modelValue) return props.modelValue

    const firstItem = props.items?.[0]

    if (!firstItem) return

    return getDefaultValue(firstItem)
}

const { context, on } = useField({
    props: () => props,
    emit,
    on: {
        enableChange: true,
        enableInput: true,
        forceValidateOnInput: props.liveValidate === LiveValidateType.INPUT,
        forceValidateOnBlur: props.liveValidate === LiveValidateType.BLUR,
        forceValidateOnChange: props.liveValidate === LiveValidateType.CHANGE
    },
    options: {
        initialValue: getInitialValue()
    }
})

onBeforeMount(() => {
    emit('update:modelValue', context.value.value)
})

watch(context.value, (value) => {
    if (!value || (typeof value === 'string' && value.length === 0)) {
        context.setValue(undefined, false)
    }
}, {
    immediate: true,
    flush: 'post'
})

defineExpose({
    context
})
</script>

<template>
    <div class="select-container">
        <label :for="props.name">
            <slot />
        </label>

        <div class="select-wrapper">
            <select
                class="select"
                :name="props.name"
                :disabled="props.disabled"
                :autocomplete="props.autocomplete"
                :autofocus="props.autofocus"
                :multiple="props.multiple"
                :size="props.size"
                :value="context.value.value"
                :data-test="props.dataTest"
                v-on="on"
            >
                <template v-for="item in props.items" :key="item.value">
                    <slot name="item" v-bind="item">
                        <!-- Option -->
                        <template v-if="item.type === 'option'">
                            <!-- eslint-disable vue/no-v-html items are safe -->
                            <option
                                class="select__option"
                                :value="item.value === undefined ? '' : item.value"
                                :selected="context.value.value === item.value"
                                v-html="item.label"
                            />
                            <!-- eslint-enable vue/no-v-html -->
                        </template>

                        <!-- Group -->
                        <template v-if="item.type === 'group'">
                            <optgroup :key="item.label" class="select__option-group" :label="item.label">
                                <template v-for="groupItem in item.items" :key="groupItem.value">
                                    <!-- eslint-disable vue/no-v-html -->
                                    <option
                                        class="select__option"
                                        :value="groupItem.value === undefined ? '' : groupItem.value"
                                        :selected="context.value.value === groupItem.value"
                                        v-html="groupItem.label"
                                    />
                                    <!-- eslint-enable vue/no-v-html -->
                                </template>
                            </optgroup>
                        </template>
                    </slot>
                </template>
            </select>
        </div>

        <!-- Errors -->
        <div class="input-field__error">
            <slot name="error">
                <UIErrorMessages :messages="context.errors.value" :data-test="props.dataTest" />
            </slot>
        </div>
    </div>
</template>

<style scoped lang="scss">
.select-container {
    .select-wrapper {
        position: relative;

        &::after {
            content: '';
            position: absolute;
            top: 50%;
            right: 5%;
            transform: translateY(-50%);
            width: 10px;
            height: 10px;
            background-color: var(--on-surface, $form-field-font-color);
            -webkit-mask-image: url('@core/assets/icons/cego-select-arrow.svg?svg');
            mask-image: url('@core/assets/icons/cego-select-arrow.svg?svg');
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            -webkit-mask-size: contain;
            mask-size: contain;
            pointer-events: none;
        }
    }

    .select {
        display: block;
        line-height: 1.428571429;
        height: 36px;
        width: 100%;
        color: var(--on-surface, $form-field-font-color);
        border-radius: var(--border-radius, $border-radius);
        min-width: 150px;
        background-color: var(--surface, $form-field-bg);
        transition:
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;

        appearance: none;

        &:focus {
            border-color: var(--border-secondary, $form-border-focus);

            // Fallback for older browsers
            box-shadow:
                inset 0 1px 1px rgba(0, 0, 0, 0.075),
                0 0 8px var(--border-secondary);
            // Modern solution for browsers with color-mix support
            box-shadow:
                inset 0 1px 1px rgba(0, 0, 0, 0.075),
                0 0 8px color-mix(in srgb, var(--border-secondary) 60%, transparent);
            outline: 0 !important;
        }
    }
}
</style>
